<template>
  <div class="section">
    <div class="container">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
        <b-button
          class="left-right"
          type="is-success"
          icon-left="plus"
          :disabled="selectedPlant === null"
          @click="showCreateForm()"
        >
          Create New Movement
        </b-button>
      </div>
      <div class="columns">
        <div class="column">
          <b-field grouped>
            <b-field label="Start Date">
              <b-datepicker
                v-model="start"
                name="startdate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="Start date"
              />
            </b-field>
            <b-field label="End Date">
              <b-datepicker
                v-model="end"
                name="enddate"
                :first-day-of-week="1"
                :locale="'en-CA'"
                icon="calendar-alt"
                placeholder="End date"
              />
            </b-field>
            <b-field v-if="hasGroupPlants" label="Plant">
              <b-select v-model="selectedPlant" icon="industry" expanded>
                <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
                  {{ plant.name }}
                </option>
              </b-select>
            </b-field>
            <b-field v-else label="Plant">
              <multiselect
                v-model="selectedPlant"
                :options="plants"
                :multiple="false"
                :close-on-select="true"
                placeholder="Pick plant"
                :custom-label="(obj) => obj.id + ' - ' + obj.name"
                track-by="name"
                style="min-width: 400px;"
              />
            </b-field>
          </b-field>
          <b-field>
            <b-button
              class="left-right"
              type="is-warning"
              icon-left="arrows-rotate"
              :disabled="isLoading || selectedPlant === null"
              @click="() => {resetData(); getData();}"
            >
              Load
            </b-button>
            <span v-if="lastProductionDate !== undefined" style="margin-left: 1rem; align-self: center; color: #7a7a7a;">
              {{ lastProductionDate ? 'Last production entered: ' + lastProductionDate : 'No production entered yet' }}
            </span>
          </b-field>
          <div v-if="missingDays && missingDays.length > 0" class="missing-days">
            <span>Missing production dates in the last {{ missingDaysSpan }} days:</span>
            <b-taglist>
              <b-tag v-for="day in missingDays" :key="day" type="is-warning is-light">
                {{ formatMissingDay(day) }}
              </b-tag>
            </b-taglist>
          </div>
          <ag-grid-vue
            v-if="movements.length != 0"
            style="width: 100%; height: 700px;"
            class="ag-theme-balham"
            :row-data="movements"
            :grid-options="gridOptions"
            :pagination="true"
            :pagination-auto-page-size="true"
            :cache-overflow-size="0"
            :multi-sort-key="'ctrl'"
            :modules="modules"
            :get-context-menu-items="getContextMenuItems"
          />
        </div>
      </div>
    </div>
    <b-modal :can-cancel="['escape', 'x']" :active.sync="isCreatingMovement" scroll="keep" has-modal-card>
      <MovementForm :plant="selectedPlant" :saving="isSaving" @submit="submitMovement" />
    </b-modal>
    <b-modal :active.sync="isEditingMovement" scroll="keep" has-modal-card>
      <form v-if="isEditingMovement" @submit.prevent="submitEdit">
        <div class="modal-card" style="min-width: 50vw; width: fit-content; min-height: 50vh; height: fit-content;">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit Movement
              <span v-if="selectedPlant" style="font-size:1rem; text-overflow: elipsis">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Crate Type">
                <b-select v-model="editableMovement.type" placeholder="Crate Type" :disabled="editIsCrossType">
                  <option v-for="type in editTypeOptions" :key="type.value" :value="type.value">
                    {{ type.name }}
                  </option>
                </b-select>
              </b-field>
              <b-field label="Date">
                <b-datepicker
                  v-model="editableMovement.date"
                  name="date"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="Date"
                />
              </b-field>
              <b-field label="Shift">
                <b-select v-model="editableMovement.shift" placeholder="Shift">
                  <option value="AM">
                    AM
                  </option>
                  <option value="PM">
                    PM
                  </option>
                  <option value="Night">
                    Night
                  </option>
                </b-select>
              </b-field>
            </b-field>
            <b-field grouped>
              <b-field label="Number of Crates">
                <b-input v-model="editableMovement.num_crates" type="number" placeholder="Crates" />
              </b-field>
            </b-field>
            <b-field grouped>
              <b-field label="Hours">
                <b-input v-model="editableMovement.hours" type="number" step="0.25" placeholder="Hours" />
              </b-field>
            </b-field>
            <b-field label="Comment">
              <b-input v-model="editableMovement.comment" type="text" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info is-fullwidth" :class="{ 'is-loading': isSaving }" :disabled="isSaving">
              {{ $t('global.submit') }}
            </button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { AllModules } from '@ag-grid-enterprise/all-modules'
import CrateMovements from '../../services/v3/crateMovements'
import { mapState } from 'vuex'
import MovementForm from './movementForm.vue'
import plantSelector from '@/mixins/plantSelector'
import saving from '@/mixins/saving'
import { restoreConsultingPlant } from '@/helpers/plants'
import ClientCrateType from '@/services/v3/clientCrateType'
import { UNIVERSAL_CRATE_TYPES, LEGACY_CLIENT_TYPES, VIRTUAL_TYPE_MEMBERS, describeType } from '@/helpers/crateTypes'
import { operationAllowedForType } from '@/helpers/movementOperations'
// import sortAndFilterMixins from '@/mixins/agGridSortAndFilter'

export default {
  name: 'CrateMovements',
  components: {
    MovementForm
  },
  mixins: [plantSelector, saving],
  data: function () {
    return {
      editableMovement: null,
      start: null,
      end: new Date(),
      selectedPlant: restoreConsultingPlant(),
      isLoading: false,
      isCreatingMovement: false,
      isEditingMovement: false,
      movements: [],
      clientCrateTypes: [],
      // Shown once a load has finished, for the plant that was loaded: 'YYYY-MM-DD',
      // null when it has never declared, undefined when nothing is to be shown.
      lastProductionDate: undefined,
      // Washing days of the last missingDaysSpan days (today excluded) that are not
      // holidays and have no declaration, for the plant that was loaded. undefined
      // when nothing is to be shown.
      missingDays: undefined,
      missingDaysSpan: 30,
      modules: AllModules,
      gridOptions: {
        cacheBlockSize: 20,
        paginationPageSize: 20,
        enableRangeSelection: true,
        defaultColDef: {
          floatingFilter: true,
          enableValue: true,
          enableRowGroup: true,
          enablePivot: false,
          resizable: true
        },
        columnDefs: [
          { headerName: 'Operation',
            field: 'operation',
            filter: 'agTextColumnFilter',
            width: 180,
            sortable: true,
            filterParams: { suppressAndOrCondition: true },
            valueGetter: (params) => {
              return params.data && params.data.operation ? params.data.operation.replace('washing', 'manual washing') : ''
            }
          },
          { headerName: 'Crates',
            field: 'num_crates',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Crate Type',
            field: 'type',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Comment',
            field: 'comment',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Hours',
            field: 'hours',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            sort: 'Desc',
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Date',
            field: 'date',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            sort: 'Desc',
            filterParams: { suppressAndOrCondition: true }
          },
          { headerName: 'Shift',
            field: 'shift',
            filter: 'agTextColumnFilter',
            width: 130,
            sortable: true,
            filterParams: { suppressAndOrCondition: true }
          }
        ],
        sideBar: {
          toolPanels: [
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                // tool panel columns won't move when columns are reordered in the grid
                suppressSyncLayoutWithGrid: true,
                // prevents columns being reordered from the Columns Tool Panel
                suppressColumnMove: true
              }
            }
          ],
          defaultToolPanel: ''
        }
      }
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    // '5.X' is a reporting pool the forecast expands away, so a movement must never
    // be rebound to it.
    crateTypeOptions () {
      return [
        ...UNIVERSAL_CRATE_TYPES.filter(t => !VIRTUAL_TYPE_MEMBERS[t.value]),
        ...LEGACY_CLIENT_TYPES,
        ...this.clientCrateTypes.map(t => ({ name: t.name, value: t.name }))
      ]
    },
    // Preparation and declassification straddle a client type and its root, so their
    // type is not one value the modal could rebind.
    editIsCrossType () {
      const movement = this.editableMovement
      return !!movement && movement.status_crate_start.split('__')[0] !== movement.status_crate_end.split('__')[0]
    },
    // Only the types the movement's operation may be declared against: a manual washing
    // rebound to a client type would land on its DIRTY stock, which nothing can ever
    // fill. The movement's own type stays listed so the select still shows it.
    editTypeOptions () {
      const movement = this.editableMovement
      if (!movement) return []
      return this.crateTypeOptions.filter(t => t.value === movement.type ||
        (!this.editIsCrossType && operationAllowedForType(movement.operation, describeType(t.value, this.clientCrateTypes))))
    }
  },
  watch: {
    // The date and the missing days belong to the plant that was loaded, not to the
    // one now picked.
    selectedPlant () {
      this.lastProductionDate = undefined
      this.missingDays = undefined
    }
  },
  mounted: async function () {
    this.start = new Date()
    this.start.setDate(new Date().getDate() - 1)
    await this.loadPlants()
  },
  methods: {
    resetData () {
      this.movements = []
    },
    showCreateForm () {
      this.isCreatingMovement = true
    },
    // A copy, so a cancelled edit leaves the grid's row as it was.
    showEditForm (movement) {
      this.editableMovement = {
        ...movement,
        date: new Date(new Date(movement.date).toISOString().split('T')[0] + 'T00:00:00')
      }
      this.isEditingMovement = true
    },
    async getData () {
      if (!this.selectedPlant || !this.selectedPlant.id) {
        this.$buefy.toast.open({ message: 'Please select a plant', duration: 3000, type: 'is-danger' })
        return
      }
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      this.isLoading = true
      const plantId = this.selectedPlant.id
      // Informative only, so its failure must not fail the load; it resolves to
      // undefined and the page shows no date.
      const lastDate = CrateMovements.getLastDate(plantId).catch(() => undefined)
      // Independent of the grid's own range, and informative only as well.
      const missingDays = this.loadMissingDays(plantId).catch(() => undefined)
      try {
        // Without the plant's client-specific types in the edit dropdown, opening and
        // saving one of their movements would silently rebind it to another type.
        this.clientCrateTypes = await ClientCrateType.getForSite(this.selectedPlant.id)
        const data = await CrateMovements.getTable({
          start: this.start.toLocaleDateString('en-CA'),
          end: this.end.toLocaleDateString('en-CA'),
          siteId: this.selectedPlant.id
        })
        data.forEach(element => {
          this.movements.push({
            id: element.id,
            id_site: element.id_site,
            operation: element.operation,
            num_crates: element.num_crates,
            status_crate_end: element.status_crate_end,
            status_crate_start: element.status_crate_start,
            type: element.status_crate_end.split('__')[0],
            shift: element.shift,
            hours: element.hours,
            date: new Date(element.date.split('Z')[0]).toLocaleDateString('en-CA'),
            comment: element.comment
          })
        })
        const lastProductionDate = await lastDate
        const missing = await missingDays
        // The plant may have changed while the requests were out, and these are not
        // the new plant's.
        if (this.selectedPlant && this.selectedPlant.id === plantId) {
          this.lastProductionDate = lastProductionDate
          this.missingDays = missing
        }
        if (this.movements.length > 0) {
          this.$buefy.toast.open({ message: 'Data loaded', duration: 3000, type: 'is-success' })
        } else {
          this.$buefy.toast.open({ message: 'No corresponding movements found', duration: 3000, type: 'is-danger' })
        }
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
        this.isLoading = false
      }
      this.isLoading = false
    },
    // Today is left out: the sites declare a day's figures the day after. Days are
    // walked at local midnight so getDay() and the 'en-CA' string are the same day.
    async loadMissingDays (plantId) {
      const end = new Date()
      end.setHours(0, 0, 0, 0)
      end.setDate(end.getDate() - 1)
      const start = new Date(end)
      start.setDate(start.getDate() - this.missingDaysSpan + 1)
      const { declaredDays, holidays, washingDays } = await CrateMovements.getCalendar({
        siteId: plantId,
        start: start.toLocaleDateString('en-CA'),
        end: end.toLocaleDateString('en-CA')
      })
      const missing = []
      for (let i = 0; i < this.missingDaysSpan; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + i)
        const day = d.toLocaleDateString('en-CA')
        if (washingDays.includes(d.getDay()) && !holidays.includes(day) && !declaredDays.includes(day)) {
          missing.push(day)
        }
      }
      return missing
    },
    formatMissingDay (day) {
      const [y, m, d] = day.split('-').map(Number)
      return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: '2-digit' })
    },
    getContextMenuItems (params) {
      const selectedMovement = params.node.data
      let customMenu = []
      const genericMenu = [
        'separator',
        'copy',
        'copyWithHeaders',
        'separator',
        'export'
      ]
      customMenu.push({
        name: 'Edit Movement',
        action: () => {
          this.showEditForm(selectedMovement)
        },
        icon: '<i class="fas fa-fw fa-edit" ></i>'
      })
      customMenu.push({
        name: 'Delete Movement',
        action: () => {
          this.deleteMovement(selectedMovement)
        },
        icon: '<i class="fas fa-fw fa-xmark" ></i>'
      })
      const result = [...customMenu, ...genericMenu]
      return result
    },
    // Every line goes in one request, saved whole or not at all.
    async submitMovement (movements) {
      if (!await this.runSave(() => CrateMovements.createMovement(movements))) {
        return
      }
      this.isCreatingMovement = false
      this.$buefy.toast.open({ message: 'Movement(s) created', duration: 3000, type: 'is-success' })
      this.resetData()
      this.getData()
    },
    // The payload is built aside, so a failed save leaves the modal open on what was
    // typed. Whether the operation suits the chosen type is the API's call as well.
    async submitEdit () {
      const movement = this.editableMovement
      const [startType, startStatus] = movement.status_crate_start.split('__')
      const [endType, endStatus] = movement.status_crate_end.split('__')
      const payload = { ...movement, date: movement.date.toLocaleDateString('en-CA') }
      if (startType !== endType) {
        if (movement.type !== endType) {
          this.$buefy.toast.open({ message: 'This movement\'s crate type cannot be changed', duration: 3000, type: 'is-danger' })
          return
        }
      } else {
        payload.status_crate_start = movement.type + '__' + startStatus
        payload.status_crate_end = movement.type + '__' + endStatus
      }
      if (!await this.runSave(() => CrateMovements.updateMovement(payload))) {
        return
      }
      this.isEditingMovement = false
      this.$buefy.toast.open({ message: 'Movement updated', duration: 3000, type: 'is-success' })
      this.resetData()
      this.getData()
    },
    async deleteMovement (movement) {
      try {
        await CrateMovements.archiveMovement(movement.id)
        this.$buefy.toast.open({ message: 'Movement Deleted', duration: 3000, type: 'is-warning' })
        this.resetData()
        this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message, duration: 3000, type: 'is-danger' })
      }
    }
  }
}

</script>

<style scoped>
.missing-days {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #7a7a7a;
}
.missing-days .tags {
  margin-bottom: 0;
}
.missing-days .tags .tag {
  margin-bottom: 0;
}
</style>
